import React from 'react';
import PropTypes from 'prop-types';
import _path from 'ramda/src/path';
import format from 'string-format';
import resx from '../../../utilities/resx';
import utilities from '../../../utilities/generalUtility';

import { RESX_LISTINGS, RESX_REBLASTS, RESX_DASHBOARD } from '../../../Constants';

const Content = props => {
    const { lazy } = props;
    const {
        ContentType,
        FileAttachmentUrl,
        HasFileInAttachment,
        HasListing,
        HasImageInAttachment,
        ListingAddress,
        ListingCity,
        ListingCoopFeeAmount,
        ListingCountry,
        ListingCurrencyCode,
        ListingFormattedPrice,
        ListingFormattedPricePerAmount,
        ListingHasImage,
        ListingInteriorSize,
        ListingInteriorSizeUnit,
        ListingIsSold,
        ListingIsLease,
        ListingNumberBedrooms,
        ListingNumberBathrooms,
        ListingPrice,
        ListingPricePerAmount,
        ListingPriceSuffix,
        ListingPropertySize,
        ListingSeoUrl,
        ListingSoldLabel,
        ListingLeaseLabel,
        ListingState,
        ListingSuiteNumber2,
        ListingLargeImageUrl,
        ListingVirtualTourAddress,
        ListingMarketStatus,
        ImageLargeAttachmentUrl,
        ReblastDescription
    } = props.data;
    const HidePreMarketDetails =
        ContentType === 'PreMarketListing' &&
        !(
            _path(['RESAAS', 'User2', 'IsProfessional'], window) &&
            !_path(['RESAAS', 'User2', 'IsPendingProfessional'], window)
        ) &&
        !ListingIsSold;

    const listingInteriorSizeUnitText = () => {
        if (ListingInteriorSizeUnit === 0) {
            return resx(RESX_LISTINGS, 'SQFT');
        }

        if (ListingInteriorSizeUnit === 1) {
            return resx(RESX_DASHBOARD, 'SQUARE_METERS_ABBR');
        }

        if (ListingInteriorSizeUnit === 2) {
            return resx(RESX_DASHBOARD, 'ACRES_UNIT_TEXT');
        }

        return resx(RESX_LISTINGS, 'SQFT');
    };

    const formatAddress = () => {
        let address = '';

        if (!HidePreMarketDetails) {
            if (ListingSuiteNumber2) {
                if (ListingCountry === 'USA') {
                    address = format('{0} #{1}', ListingAddress, ListingSuiteNumber2);
                } else {
                    address = format('{0} - {1}', ListingSuiteNumber2, ListingAddress);
                }
            } else {
                address = ListingAddress;
            }
        }

        if (ListingCity) {
            address = HidePreMarketDetails ? ListingCity : `${address}, ${ListingCity}`;
        }
        if (ListingState) {
            address = `${address}, ${ListingState}`;
        }
        if (ListingCountry) {
            address = `${address}, ${ListingCountry}`;
        }

        return address;
    };

    const vidUrl = _path(['vidUrl'], utilities.generateVideoPlayerIframe(ReblastDescription, lazy));
    let virtualTourAddressVidUrl;
    const virtualTourAddressVidIframe = utilities.generateVideoPlayerIframe(ListingVirtualTourAddress, lazy);
    if (Object.keys(virtualTourAddressVidIframe).length > 0) {
        let videoObj = {};
        videoObj = virtualTourAddressVidIframe;

        virtualTourAddressVidUrl = videoObj.vidUrl;
    }

    const formattedAddress = formatAddress();
    const listingImageAlt = `${resx(RESX_REBLASTS, 'LISTING_LARGE_IMAGE_ALT')} ${formattedAddress}`;

    let listingImageSrc;
    let listingImageSrcSet;

    if (ListingLargeImageUrl) {
        listingImageSrc = utilities.addParamsToUrlQueryString(ListingLargeImageUrl, {
            width: 634,
            height: 357,
            mode: 'crop',
            quality: 80
        });
        listingImageSrcSet = `${utilities.addParamsToUrlQueryString(ListingLargeImageUrl, {
            width: 320,
            height: 180,
            mode: 'crop',
            quality: 80
        })} 320w, ${utilities.addParamsToUrlQueryString(ListingLargeImageUrl, {
            width: 634,
            height: 357,
            mode: 'crop',
            quality: 80
        })} 634w, ${utilities.addParamsToUrlQueryString(ListingLargeImageUrl, {
            width: 1268,
            height: 714,
            mode: 'crop',
            quality: 80
        })} 1268w`;
    }

    const contentImg = (src, srcset, className, alt, sizes, testid) => {
        const imgProps = {};

        if (lazy) {
            if (src) {
                imgProps['data-src'] = src;
            }
            if (srcset) {
                imgProps['data-srcset'] = srcset;
            }

            if (className) {
                imgProps.className = `${className} lazyload`;
            } else {
                imgProps.className = 'lazyload';
            }
        } else {
            if (src) {
                imgProps.src = src;
            }
            if (srcset) {
                imgProps.srcSet = srcset;
            }
            if (className) {
                imgProps.className = className;
            }
        }

        if (alt) {
            imgProps.alt = alt;
        }
        if (sizes) {
            imgProps.sizes = sizes;
        }
        if (testid) {
            imgProps['data-testid'] = testid;
        }

        return <img {...imgProps} />;
    };

    const renderListingImage = () => {
        // Image is google map do not add additional params as it will invalidate signature
        if (ListingLargeImageUrl && ListingLargeImageUrl.includes('googleapis')) {
            return contentImg(ListingLargeImageUrl, null, 'listing-image', listingImageAlt, null, null);
        }
        return contentImg(
            listingImageSrc,
            listingImageSrcSet,
            'listing-image',
            listingImageAlt,
            '(min-width: 25em) 634px, 350px',
            'listingimage'
        );
    };

    const renderContentImage = () => {
        return contentImg(
            `${ImageLargeAttachmentUrl}?width=634&quality=80`,
            `${ImageLargeAttachmentUrl}?width=320&quality=80 320w,${ImageLargeAttachmentUrl}?width=634&quality=80 634w,${ImageLargeAttachmentUrl}?width=1268&quality=80&scale=both 1268w`,
            'bg-attachment-photo reblast-photo-exists',
            resx(RESX_REBLASTS, 'IMAGE_ATTACHMENT_ALT'),
            '(min-width: 25em) 634px, 350px',
            null
        );
    };

    return (
        <div className="re-reblast-content">
            <p
                className="text reblast-message"
                dangerouslySetInnerHTML={{ __html: ReblastDescription }}
                data-testid="text"
            />
            {vidUrl && <div dangerouslySetInnerHTML={{ __html: vidUrl }} />}

            {HasImageInAttachment && (
                <div className="reblast-attachment" data-testid="attachment">
                    <div className="reblast-attachment-photo">
                        <a
                            href={`${ImageLargeAttachmentUrl}?width=800&quality=80`}
                            data-href={`${ImageLargeAttachmentUrl}?width=800&quality=80`}
                            rel="prettyPhoto"
                        >
                            {renderContentImage()}
                        </a>
                    </div>
                </div>
            )}
            {HasFileInAttachment && (
                <div className="reblast-attachment" data-testid="attachment">
                    <div className="reblast-attachment-document document-exists">
                        <a href={FileAttachmentUrl} target="_blank" rel="noopener noreferrer">
                            <div className="bg-attachment-document" />
                            <h4>{resx(RESX_REBLASTS, 'PDF_BUTTON_TEXT')}</h4>
                        </a>
                    </div>
                </div>
            )}
            {HasListing && ListingHasImage && (
                <div className="reblast-listing-container has-associated-listing">
                    <div
                        className={`listing-image-wrapper ${
                            HidePreMarketDetails ? 'listing-image-wrapper-hidepremarketdetails' : ''
                        }`}
                        data-testid="listingimage"
                    >
                        <a href={ListingSeoUrl} className="reblasts-listing-image seo-link reblast-listing-link">
                            {virtualTourAddressVidUrl && !HidePreMarketDetails ? (
                                <div
                                    className="reblast-listing-video"
                                    data-listingimage={ListingLargeImageUrl}
                                    data-testid="ReblastListingWithVideo"
                                    dangerouslySetInnerHTML={{ __html: virtualTourAddressVidUrl }}
                                />
                            ) : (
                                renderListingImage()
                            )}
                        </a>
                    </div>
                    <div
                        className={`re-reblast-listing-details listing-details clearfix ${
                            virtualTourAddressVidUrl && !HidePreMarketDetails
                                ? 'listing-details-with-video-position'
                                : ''
                        }`}
                        data-testid="listingdetails"
                    >
                        <a
                            href={ListingSeoUrl}
                            className={`reblasts-listing-image seo-link ${
                                virtualTourAddressVidUrl && !HidePreMarketDetails
                                    ? 're-listing-details-link-with-video'
                                    : ''
                            }`}
                        >
                            {ContentType === 'PreMarketListing' && (
                                <div className="re-post-label re-post-label-premarket" data-testid="premarketlabel">
                                    <span>{resx(RESX_LISTINGS, 'PUBLIC_PRE_MARKET_LISTING_LABEL')}</span>
                                </div>
                            )}

                            {parseInt(ListingMarketStatus, 10) === 2 ? (
                                <div className="re-post-label re-post-label-premarket" data-testid="offmarket">
                                    <span>{resx(RESX_LISTINGS, 'OFFMARKET_LABEL')}</span>
                                </div>
                            ) : null}

                            {ListingIsSold ? (
                                <div
                                    className="re-post-label re-reblast-listing-label listing-sold"
                                    data-testid="soldlabel"
                                >
                                    {ListingSoldLabel}
                                </div>
                            ) : null}

                            {ListingIsLease && !ListingIsSold ? (
                                <div
                                    className="re-post-label re-reblast-listing-label listing-lease"
                                    data-testid="leaselabel"
                                >
                                    {resx(RESX_LISTINGS, 'TRANSACTION_TYPE_FOR_LEASE_TEXT')}
                                </div>
                            ) : null}

                            <ul className="re-reblast-listing-details-primary">
                                {!HidePreMarketDetails && !ListingPricePerAmount && ListingPrice > 0 ? (
                                    <li className="listing-details-price" data-testid="pricefformatted">
                                        {ListingFormattedPrice} {ListingCurrencyCode}
                                    </li>
                                ) : null}
                                {!HidePreMarketDetails && ListingPricePerAmount ? (
                                    <li className="listing-details-price" data-testid="pricepersqf">
                                        {ListingFormattedPricePerAmount} {ListingCurrencyCode} /{' '}
                                        {resx(RESX_LISTINGS, 'SQFT')}
                                    </li>
                                ) : null}
                                {ListingNumberBedrooms && ListingNumberBedrooms !== 0 ? (
                                    <li className="listing-detail-bedrooms">
                                        {ListingNumberBedrooms} {resx(RESX_LISTINGS, 'BEDROOMS_SHORT_TEXT')}
                                    </li>
                                ) : (
                                    ''
                                )}
                                {ListingNumberBathrooms && ListingNumberBathrooms !== 0 ? (
                                    <li className="listing-detail-bathrooms">
                                        {ListingNumberBathrooms} {resx(RESX_LISTINGS, 'BATHROOMS_SHORT_TEXT')}
                                    </li>
                                ) : (
                                    ''
                                )}
                                {ListingInteriorSize && ListingInteriorSize !== 0 ? (
                                    <li className="listing-detail-propertysize">
                                        {ListingInteriorSize} {listingInteriorSizeUnitText()}
                                    </li>
                                ) : (
                                    ''
                                )}
                                {ListingPropertySize && ListingPropertySize !== 0 ? (
                                    <li className="listing-detail-propertysize">
                                        {ListingPropertySize} {resx(RESX_LISTINGS, 'SQFT')}
                                    </li>
                                ) : (
                                    ''
                                )}
                                {ListingCoopFeeAmount && ListingCoopFeeAmount !== 0 ? (
                                    <li className="listing-detail-coopfee">
                                        {ListingCoopFeeAmount}% {resx(RESX_LISTINGS, 'COOP_FEE')}
                                    </li>
                                ) : (
                                    ''
                                )}
                            </ul>
                            <span>{formattedAddress}</span>
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
};

Content.propTypes = {
    data: PropTypes.object.isRequired,
    lazy: PropTypes.bool
};

export default Content;
